import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecordRouteApi } from '../Services/RecordRouteApiProvider';
import { useAlert } from './AlertContext';
import ListSelect from './ListSelect';

const CONNECTION_OBJECT_OPTIONS_LIMIT = 50;

export function useConnectionObject(type, id) {
  const { getConnectionObjectById } = useRecordRouteApi();
  const { showAlert } = useAlert();
  const [result, setResult] = useState({ id: null, data: null });

  useEffect(() => {
    if (type !== 'connection_object' || !id) return undefined;

    let cancelled = false;
    getConnectionObjectById(id)
      .then((data) => !cancelled && setResult({ id, data }))
      .catch(() => {
        if (cancelled) return;
        setResult({ id, data: null });
        showAlert('fail', 'Failed to load the selected connection object');
      });
    return () => {
      cancelled = true;
    };
  }, [type, id, getConnectionObjectById, showAlert]);

  const isActive = type === 'connection_object' && Boolean(id);
  return {
    connectionObject: isActive && result.id === id ? result.data : null,
    isLoading: isActive && result.id !== id,
  };
}

export default function ConnectionObjectSelect({
  id,
  value,
  onChange,
  disabled,
  selectedName,
  selectedLoading,
}) {
  const { getConnectionObjectOptions } = useRecordRouteApi();
  const { showAlert } = useAlert();
  const [page, setPage] = useState({
    options: [],
    fetched: 0,
    hasMore: false,
    query: '',
    loaded: false,
  });
  const [loading, setLoading] = useState(false);
  const [unfilteredCount, setUnfilteredCount] = useState();
  const requestIdRef = useRef(0);
  const queryRef = useRef('');
  const pageRef = useRef(page);
  pageRef.current = page;
  const loadingRef = useRef(false);

  const fetchPage = useCallback(
    async (name, skip) => {
      const requestId = ++requestIdRef.current;
      const isCurrent = () => requestId === requestIdRef.current;
      loadingRef.current = true;
      setLoading(true);
      if (skip === 0) {
        setPage({ options: [], fetched: 0, hasMore: false, query: name, loaded: false });
      }
      try {
        const { items, totalCount } = await getConnectionObjectOptions(
          skip,
          CONNECTION_OBJECT_OPTIONS_LIMIT,
          name,
        );
        if (!isCurrent()) return;
        if (!name) setUnfilteredCount(totalCount);
        setPage((prev) => {
          const base = skip === 0 ? [] : prev.options;
          const seen = new Set(base.map((option) => option.value));
          const next = items
            .map((item) => ({ value: item.key, label: item.name }))
            .filter((option) => !seen.has(option.value));
          const fetched = skip + items.length;
          return {
            options: [...base, ...next],
            fetched,
            hasMore: items.length > 0 && fetched < totalCount,
            query: name,
            loaded: true,
          };
        });
      } catch {
        if (isCurrent()) showAlert('fail', 'Failed to load connection objects');
      } finally {
        if (isCurrent()) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [getConnectionObjectOptions, showAlert],
  );

  const handleSearch = useCallback(
    (name) => {
      queryRef.current = name;
      const { loaded, query } = pageRef.current;
      if (loaded && query === name) return;
      fetchPage(name, 0);
    },
    [fetchPage],
  );

  const handleLoadMore = useCallback(() => {
    if (loadingRef.current) return;
    fetchPage(queryRef.current, pageRef.current.fetched);
  }, [fetchPage]);

  const options = useMemo(() => {
    if (!value || !selectedName || page.query) return page.options;
    return [
      { value, label: selectedName },
      ...page.options.filter((option) => option.value !== value),
    ];
  }, [value, selectedName, page.options, page.query]);

  return (
    <ListSelect
      id={id}
      label="Name"
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      loading={loading}
      fallbackLabel={selectedLoading ? 'Loading…' : selectedName || value}
      onSearch={handleSearch}
      onLoadMore={handleLoadMore}
      hasMore={page.hasMore}
      totalCount={unfilteredCount}
      searchable
      panelWidth={380}
    />
  );
}
